import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, CheckCircle2, Database, BarChart2, Brain, Zap,
  Globe, Clock, Award, TrendingUp, Shield, Rocket
} from 'lucide-react';

const AIConsultingUK = () => {
  const metrics = [
    { value: '3–8 Weeks', label: 'Production-Ready Deployment', description: 'From kickoff to live system' },
    { value: '60–80%', label: 'Faster', description: 'Than traditional UK consulting firms' },
    { value: '99.9%', label: 'Data Accuracy', description: 'Across all implementations' },
    { value: '5★ Clutch', label: 'Rating', description: 'Verified UK client satisfaction' },
  ];

  const services = [
    {
      icon: Database,
      title: 'Data Engineering & Warehousing',
      description: 'Modern cloud data infrastructure built for scale — from legacy migration to production-grade pipelines your analysts can trust.',
      outcomes: [
        'Legacy database migration with zero data loss',
        'Snowflake and Azure Synapse warehouse builds',
        'ETL pipelines via Azure Data Factory and Apache Airflow',
        'Data quality frameworks and automated validation',
      ],
    },
    {
      icon: BarChart2,
      title: 'Business Intelligence & Analytics',
      description: 'Turn your data into decisions with enterprise dashboards, automated reporting, and real-time visibility across the business.',
      outcomes: [
        'Power BI and Tableau dashboard development',
        'Real-time KPI dashboards for executive teams',
        'Automated report delivery via Power Automate',
        'Self-service analytics for non-technical users',
      ],
    },
    {
      icon: Brain,
      title: 'AI & Machine Learning',
      description: 'Predictive models and intelligent systems that solve real business problems — not research projects that never ship.',
      outcomes: [
        'Predictive analytics and demand forecasting',
        'Recommendation engines and personalisation systems',
        'NLP and Document AI for unstructured data',
        'Generative AI integration (Claude, GPT-4, Mistral)',
      ],
    },
    {
      icon: Zap,
      title: 'Process Automation',
      description: 'Eliminate manual effort at scale with intelligent automation — workflows, document processing, and system integration built for enterprise environments.',
      outcomes: [
        'Make.com and Power Automate workflow design',
        'Document processing and data extraction pipelines',
        'CRM and ERP integration and automation',
        'Compliance and audit trail automation',
      ],
    },
  ];

  const techStack = [
    { category: 'Cloud & Infrastructure', items: ['Azure', 'AWS', 'GCP'] },
    { category: 'BI & Data Warehousing', items: ['Power BI', 'Tableau', 'Snowflake'] },
    { category: 'AI & Machine Learning', items: ['Python', 'XGBoost', 'TensorFlow'] },
    { category: 'Automation & Pipelines', items: ['Power Automate', 'Azure Data Factory', 'Make.com'] },
  ];

  const industries = [
    { name: 'Retail & Pharmacy', detail: 'Inventory analytics, demand forecasting, and patient data platforms' },
    { name: 'Healthcare & Life Sciences', detail: 'Clinical data pipelines, risk modelling, and compliance reporting' },
    { name: 'Financial Services & Insurance', detail: 'Underwriting analytics, claims automation, and regulatory BI' },
    { name: 'Manufacturing & FMCG', detail: 'Supply chain intelligence, production analytics, and ERP integration' },
  ];

  const whyChoose = [
    { icon: Rocket, title: 'Production-Grade Speed', description: '3–8 week delivery timelines with working software at every sprint — not slide decks.' },
    { icon: Award, title: 'No Bloated Consulting Overhead', description: 'Senior engineers directly engaged on your project. No account managers in the middle.' },
    { icon: Shield, title: 'Azure-First Expertise', description: 'Azure is our primary platform. Deep experience in ADF, Synapse, Power BI, and Azure SQL.' },
    { icon: TrendingUp, title: 'Proven UK Track Record', description: '5★ Clutch rating from verified UK clients across pharmacy, FMCG, and financial services.' },
  ];

  const faqs = [
    {
      q: 'Do you work with UK clients remotely?',
      a: 'Yes. All engagements are delivered remotely with UK business hours response times. Our team is available for calls during standard UK working hours and we use async communication to minimise friction.',
    },
    {
      q: 'Are you familiar with UK data compliance?',
      a: 'Yes. GDPR-compliant architectures are standard across every engagement — not an add-on. We design for data residency, access control, and audit logging from day one.',
    },
    {
      q: 'What is your typical engagement size?',
      a: 'We handle a wide range of scopes: 4–8 week BI implementations through to 12–20 week full data platform builds. We scope clearly upfront so you know exactly what you\'re getting.',
    },
    {
      q: 'Can you work with our existing Azure environment?',
      a: 'Yes. Azure is our primary platform and we\'re comfortable working within established tenants, existing governance structures, and current tooling. We extend what you have rather than ripping it out.',
    },
    {
      q: 'Do you offer ongoing support after delivery?',
      a: 'Yes. We offer post-delivery support agreements and managed service arrangements for clients who want ongoing platform management, enhancement work, or SLA-backed operations.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>AI Consulting Services — United Kingdom | Agility</title>
        <meta
          name="description"
          content="Production-ready AI systems, data engineering, and intelligent automation for UK enterprises. Azure-first, GDPR-compliant, delivered in 3–8 weeks."
        />
        <link rel="canonical" href="https://www.agilitytech.ai/ai-consulting-uk" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="AI Consulting Services — United Kingdom | Agility" />
        <meta property="og:description" content="Production-ready AI systems, data engineering, and intelligent automation for UK enterprises. Azure-first, GDPR-compliant, delivered in 3–8 weeks." />
        <meta property="og:url" content="https://www.agilitytech.ai/ai-consulting-uk" />
        <meta property="og:image" content="https://www.agilitytech.ai/agility hex fonts.png" />
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-900 via-black to-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-blue-500 via-transparent to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full mb-8">
            <Globe className="w-4 h-4 text-blue-400" />
            <span className="text-blue-300 text-sm font-medium">AI Consulting — United Kingdom</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            AI Consulting<br />
            <span className="text-teal-400">Built for UK Enterprises</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            Production-ready AI systems, data engineering, and intelligent automation for UK businesses.
            Azure-first, GDPR-compliant, and delivered in 3–8 weeks — not 12-month consulting cycles.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all"
            >
              Schedule a Discovery Call
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/case-studies"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold border border-white/20 transition-all"
            >
              View Case Studies
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {metrics.map((m, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-teal-400 mb-1">{m.value}</div>
                <div className="text-white font-semibold mb-1 text-sm">{m.label}</div>
                <div className="text-gray-400 text-xs">{m.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What We Build for UK Enterprises
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Data platforms, AI systems, and automation — scoped for speed and delivered to production.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="bg-gray-50 rounded-xl p-8 border border-gray-200 hover:border-teal-400 transition-all">
                  <Icon className="w-10 h-10 text-teal-600 mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{s.title}</h3>
                  <p className="text-gray-600 mb-5 text-sm leading-relaxed">{s.description}</p>
                  <ul className="space-y-2">
                    {s.outcomes.map((o, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                        {o}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Technology Stack</h2>
            <p className="text-lg text-gray-600">Azure-first delivery with best-in-class tooling across every layer of the data and AI stack.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {techStack.map((t, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">{t.category}</div>
                <ul className="space-y-2">
                  {t.items.map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-gray-800 text-sm font-medium">
                      <div className="w-1.5 h-1.5 bg-teal-500 rounded-full" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Industries We Serve</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Deep domain knowledge across the UK sectors where data and AI create the most competitive advantage.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industries.map((ind, i) => (
              <div key={i} className="bg-gradient-to-br from-teal-50 to-white rounded-xl p-6 border border-teal-100">
                <div className="text-teal-700 font-bold mb-2">{ind.name}</div>
                <p className="text-gray-600 text-sm leading-relaxed">{ind.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Why UK Enterprises Choose <span className="text-teal-400">Agility</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Senior engineers, direct engagement, and production delivery — not consulting theatre.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChoose.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-white/5 rounded-xl p-8 border border-white/10 hover:border-teal-500/50 transition-all">
                  <Icon className="w-10 h-10 text-teal-400 mb-4" />
                  <h3 className="text-lg font-bold mb-3">{item.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Common Questions from UK Clients
          </h2>
          <div className="space-y-6">
            {faqs.map((f, i) => (
              <div key={i} className="bg-white rounded-xl p-8 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{f.q}</h3>
                <p className="text-gray-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Related Services</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'AI Implementation Services', href: '/ai-implementation-services' },
              { label: 'Generative AI Consulting', href: '/generative-ai-consulting' },
              { label: 'FMCG AI Solutions', href: '/fmcg-ai' },
              { label: 'Pharma Marketing Analytics', href: '/pharmaceutical-marketing-analytics' },
              { label: 'AI Consulting India', href: '/ai-consulting-india' },
              { label: 'AI Consulting USA', href: '/ai-consulting-company-usa' },
              { label: 'Hire AI Developers', href: '/hire-ai-developers' },
              { label: 'Case Studies', href: '/case-studies' },
            ].map((link, i) => (
              <Link
                key={i}
                to={link.href}
                className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium text-sm group"
              >
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-blue-700 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Build Production AI for Your UK Business?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Tell us your challenge. We'll scope a delivery plan and respond within 48 hours.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-blue-700 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all text-lg"
          >
            Schedule a Discovery Call
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default AIConsultingUK;
